import { observer, useLocalObservable } from "mobx-react-lite"
import { runInAction } from "mobx"

import type { BurnPositionStore } from "@/domains/thick-v1/stores/burn-position-store"

import { BurnPositionView } from "./burn-position-view"

/**
 * Observer wrapper around `BurnPositionStore`.
 *
 * The confirmation-dialog open flag is transient UI state that doesn't need
 * to survive navigation, so it lives in a tiny local observable rather than
 * on the store. Everything else — the `confirmed` checkbox, expected amounts,
 * slippage, submission lifecycle — is owned by the store.
 */
export const BurnPositionComposite = observer(({ store }: { store: BurnPositionStore }) => {
  const ui = useLocalObservable(() => ({
    confirmDialogOpen: false,
    setOpen(next: boolean) {
      this.confirmDialogOpen = next
    },
  }))

  const status = store.resolvedStatus
  if (status.kind !== "found") {
    return (
      <div className="rounded-lg border border-p400-light bg-p400 p-4 text-t300 text-sm">
        {status.kind === "loading" && "Loading position..."}
        {status.kind === "missing" && "This position was not found or is not owned by the connected wallet."}
        {status.kind === "idle" && "Select a position to continue."}
      </div>
    )
  }

  const position = status.position

  // Burn flow executes `decreaseLiquidity + collect + burn`, so receive
  // preview should include both withdrawn principal and pending fees.
  const receive0 = store.expectedAmount0 + (position.pendingFees0 ?? 0n)
  const receive1 = store.expectedAmount1 + (position.pendingFees1 ?? 0n)

  const amount0Display = position.pool.token0.formatUnits(receive0)
  const amount1Display = position.pool.token1.formatUnits(receive1)

  return (
    <BurnPositionView
      token0Symbol={position.pool.token0.symbol}
      token1Symbol={position.pool.token1.symbol}
      amount0Display={amount0Display}
      amount1Display={amount1Display}
      positionValueUsd={position.positionValueUsd}
      claimableFeesUsd={position.claimableFeesUsd}
      slippagePercent={store.slippage}
      confirmed={store.confirmed}
      onToggleConfirmed={(next) => store.setConfirmed(next)}
      canSubmit={store.canSubmit}
      isSubmitting={store.isSubmitting}
      submissionError={store.submissionError}
      confirmDialogOpen={ui.confirmDialogOpen}
      onChangeConfirmDialogOpen={(next) => runInAction(() => ui.setOpen(next))}
      onSubmit={() => {
        runInAction(() => ui.setOpen(false))
        void store.submit()
      }}
      nfpmTokenIdPending={position.nfpmTokenIdPending}
    />
  )
})
